/**
 * Stale Deploy Recovery
 *
 * Detects client errors that indicate a stale deploy / client-server build
 * mismatch (App Router cache null, hook mismatch from a swapped build, or a
 * chunk that no longer exists after a redeploy) and recovers with a single,
 * cooldown-guarded hard reload so the user picks up the fresh build instead of
 * being stranded on an error screen.
 *
 * Primary responsibilities:
 * - Classify an error as a likely stale-deploy artifact
 * - Trigger at most one recovery reload within a cooldown window
 */

// Signatures of a stale-deploy / build-mismatch rather than an app bug.
// Keep this list chunk/router/cache-shaped; hydration codes alone are not
// enough evidence of a stale deploy.
const STALE_DEPLOY_ERROR_PATTERNS = [
  'parallelRoutes',
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Minified React error #310'
]

const STALE_DEPLOY_RELOAD_KEY = 'losi:stale-deploy-reloaded-at'
const STALE_DEPLOY_COOKIE = 'losi_stale_deploy_reloaded_at'
// Only auto-reload once per window so a genuinely persistent error cannot trap
// the user in a reload loop.
const STALE_DEPLOY_RELOAD_COOLDOWN_MS = 60_000

// In-memory fallback when sessionStorage is blocked (private mode / quota).
let memoryReloadedAt = 0

// --------------

export function isLikelyStaleDeployError(
  error: (Error & { digest?: string }) | null | undefined
): boolean {
  if (!error) return false
  const haystack = `${error.name ?? ''} ${error.message ?? ''} ${error.stack ?? ''}`
  return STALE_DEPLOY_ERROR_PATTERNS.some(pattern => haystack.includes(pattern))
}

function isWithinCooldown(last: number, now: number): boolean {
  return Number.isFinite(last) && last > 0 && now - last < STALE_DEPLOY_RELOAD_COOLDOWN_MS
}

function readCookieTimestamp(name: string): number {
  if (typeof document === 'undefined') return 0
  const match = document.cookie
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(`${name}=`))
  if (!match) return 0
  const value = Number(match.slice(name.length + 1))
  return Number.isFinite(value) ? value : 0
}

function writeCookieTimestamp(name: string, value: number): void {
  if (typeof document === 'undefined') return
  const maxAge = Math.ceil(STALE_DEPLOY_RELOAD_COOLDOWN_MS / 1000)
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`
}

function readLastReloadAt(): number {
  try {
    const fromStorage = Number(
      window.sessionStorage.getItem(STALE_DEPLOY_RELOAD_KEY) || '0'
    )
    if (Number.isFinite(fromStorage) && fromStorage > 0) return fromStorage
  } catch {
    // fall through to cookie / memory
  }
  const fromCookie = readCookieTimestamp(STALE_DEPLOY_COOKIE)
  if (fromCookie > 0) return fromCookie
  return memoryReloadedAt
}

function markReloadedAt(now: number): void {
  memoryReloadedAt = now
  try {
    window.sessionStorage.setItem(STALE_DEPLOY_RELOAD_KEY, String(now))
  } catch {
    // sessionStorage blocked; cookie survives reload when memory cannot.
  }
  writeCookieTimestamp(STALE_DEPLOY_COOKIE, now)
}

// Returns true when it triggered a reload (caller should stop and let the page
// reload); false when recovery is on cooldown (caller should surface the error).
export function tryRecoverFromStaleDeploy(): boolean {
  if (typeof window === 'undefined') return false

  const now = Date.now()
  const last = readLastReloadAt()
  if (isWithinCooldown(last, now)) {
    return false
  }

  markReloadedAt(now)
  window.location.reload()
  return true
}

/** Manual Retry for stale-deploy errors: always hard-reload (bypass soft reset). */
export function hardReloadForStaleDeploy(): void {
  if (typeof window === 'undefined') return
  window.location.reload()
}
